import { DownloadOutlined, InboxOutlined } from "@ant-design/icons";
import {
  Button,
  Collapse,
  Descriptions,
  Input,
  Layout,
  message,
  Space,
  Typography,
  UploadFile,
  UploadProps,
} from "antd";
import Dragger from "antd/es/upload/Dragger";
import React, { useEffect, useRef, useState } from "react";

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { Panel } = Collapse;

const HomePage: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [statusCheckId, setStatusCheckId] = useState("");
  const [taskData, setTaskData] = useState<any | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [downloadingVCF, setDownloadingVCF] = useState(false);
 
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, []);

  const checkTaskStatus = async (customId?: string, autoPoll = false) => {
    const idToCheck = customId || statusCheckId;
    if (!idToCheck) {
      message.warning("Enter a task ID");
      return;
    }

    setCheckingStatus(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_IMPUTE_ENDPOINT}/status/${idToCheck}`
      );
      if (!res.ok) throw new Error("Failed to get task status");

      const data = await res.json();
      setTaskData(data);

      if (data.status === "completed") {
        message.success("Task completed. Files ready.");
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } else if (data.status === "started") {
        if (!autoPoll) message.info("Task is in progress...");
        if (autoPoll && !pollingIntervalRef.current) {
          pollingIntervalRef.current = setInterval(() => {
            checkTaskStatus(idToCheck, true);
          }, 5000);
        }
      } else if (data.status === "queue") {
        message.info("Task is in queue.");
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    } catch (error) {
      message.error("Error checking task status.");
    } finally {
      setCheckingStatus(false);
    }
  };

  const downloadFile = async (path: string, label: string) => {
    try {
      setDownloadingVCF(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_IMPUTE_ENDPOINT}/download/${path}`
      );
      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const filename = path.split("/").pop() || `${label}.gz`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success(`${label} download started`);
    } catch {
      message.error(`Failed to download ${label}`);
    } finally {
      setDownloadingVCF(false);
    }
  };

  const getElapsed = (start: string, end: string): string => {
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).getTime();
    const diffMs = endTime - startTime;
    if (isNaN(diffMs) || diffMs < 0) return "N/A";

    const seconds = Math.floor((diffMs / 1000) % 60);
    const minutes = Math.floor((diffMs / (1000 * 60)) % 60);
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    return `${hours}h ${minutes}m ${seconds}s`;
  };

  const props: UploadProps = {
    name: "file",
    customRequest: async ({ file, onSuccess, onError }) => {
      const realFile = file as File;
      const isAllowed =
        realFile.type === "text/plain" || realFile.name.endsWith(".gz");

      if (!isAllowed) {
        message.error("Only .txt or .gz files are allowed.");
        onError?.(new Error("Invalid file type"));
        return;
      }

      setUploading(true);
      setTaskId(null);
      setTaskData(null);

      try {
        const formData = new FormData();
        formData.append("file", realFile);

        const imputeRes = await fetch(
          `${process.env.REACT_APP_BACKEND_IMPUTE_ENDPOINT}/impute/`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!imputeRes.ok) throw new Error("Upload failed");

        const { task_id } = await imputeRes.json();
        setTaskId(task_id);
        setStatusCheckId(task_id);
        message.success("File uploaded. Task created.");
        checkTaskStatus(task_id, false);

        onSuccess?.("ok");
      } catch (error) {
        console.error(error);
        message.error(`${realFile.name} upload failed.`);
        onError?.(error as Error);
      } finally {
        setUploading(false);
      }
    },
    onChange(info) {
      setFileList(info.fileList);
    },
    onRemove: () => {
      setFileList([]);
    },
    fileList,
    multiple: false,
    showUploadList: true,
  };

  const renderStepsSection = () => {
    const steps = taskData?.logs || [];
    if (steps.length === 0) return null;

    const firstLog = steps[0];
    const lastLog = steps[steps.length - 1];

    return (
      <>
        <Title level={5} style={{ marginTop: 32 }}>
          Most Recent Step
        </Title>
        <Descriptions
          bordered
          size="small"
          column={1}
          labelStyle={{ width: 150 }}
          contentStyle={{ width: 150 }}
        >
          <Descriptions.Item label="Step">{lastLog.step}</Descriptions.Item>
          <Descriptions.Item label="Message">
            {lastLog.message}
          </Descriptions.Item>
          <Descriptions.Item label="Status">{lastLog.status}</Descriptions.Item>
          <Descriptions.Item label="Start Time">
            {new Date(lastLog.created_at).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>

        {steps.length > 1 && (
          <Collapse style={{ marginTop: 24 }}>
            <Panel header="Show All Steps" key="1">
              {steps.map(
                (
                  step: {
                    created_at: any;
                    updated_at: any;
                    id: React.Key | null | undefined;
                    step: any;
                    message:
                      | string
                      | number
                      | bigint
                      | boolean
                      | React.ReactElement<
                          unknown,
                          string | React.JSXElementConstructor<any>
                        >
                      | Iterable<React.ReactNode>
                      | React.ReactPortal
                      | Promise<
                          | string
                          | number
                          | bigint
                          | boolean
                          | React.ReactPortal
                          | React.ReactElement<
                              unknown,
                              string | React.JSXElementConstructor<any>
                            >
                          | Iterable<React.ReactNode>
                          | null
                          | undefined
                        >
                      | null
                      | undefined;
                    status:
                      | string
                      | number
                      | bigint
                      | boolean
                      | React.ReactElement<
                          unknown,
                          string | React.JSXElementConstructor<any>
                        >
                      | Iterable<React.ReactNode>
                      | React.ReactPortal
                      | Promise<
                          | string
                          | number
                          | bigint
                          | boolean
                          | React.ReactPortal
                          | React.ReactElement<
                              unknown,
                              string | React.JSXElementConstructor<any>
                            >
                          | Iterable<React.ReactNode>
                          | null
                          | undefined
                        >
                      | null
                      | undefined;
                  },
                  idx: number
                ) => {
                  const nextStep = steps[idx + 1];
                  const startTime = step.created_at;
                  const endTime = nextStep?.created_at || step.updated_at;
                  const stepDuration = getElapsed(startTime, endTime);

                  return (
                    <Descriptions
                      bordered
                      size="small"
                      column={2}
                      style={{ marginBottom: 16 }}
                      key={step.id}
                      title={`Step ${step.step}`}
                    >
                      <Descriptions.Item label="Message" span={2}>
                        {step.message}
                      </Descriptions.Item>
                      <Descriptions.Item label="Status">
                        {step.status}
                      </Descriptions.Item>
                      <Descriptions.Item label="Elapsed Time">
                        {stepDuration}
                      </Descriptions.Item>
                      <Descriptions.Item label="Start Time">
                        {new Date(startTime).toLocaleString()}
                      </Descriptions.Item>
                      <Descriptions.Item label="End Time">
                        {new Date(endTime).toLocaleString()}
                      </Descriptions.Item>
                    </Descriptions>
                  );
                }
              )}
            </Panel>
          </Collapse>
        )}

        <Title level={5} style={{ marginTop: 32 }}>
          Overall Task Duration
        </Title>
        <Descriptions
          bordered
          size="small"
          column={1}
          labelStyle={{ width: 150 }}
          contentStyle={{ width: 150 }}
        >
          <Descriptions.Item label="Start Time">
            {new Date(firstLog.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="End Time">
            {new Date(lastLog.updated_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Total Elapsed Time">
            {getElapsed(firstLog.created_at, lastLog.updated_at)}
          </Descriptions.Item>
        </Descriptions>
      </>
    );
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Header style={{ backgroundColor: "#001529" }}>
        <Title level={3} style={{ color: "white" }}>
          Stadium Science
        </Title>
      </Header>
      <Content style={{ padding: "2rem" }}>
        <div style={{ background: "#fff", padding: 24, minHeight: 280 }}>
          <Title level={4}>1. Upload Test File</Title>
          <Dragger {...props} disabled={uploading}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Only .txt or .gz files are accepted. You’ll receive a task ID
              after upload.
            </p>
          </Dragger>

          {taskId && (
            <div style={{ marginTop: 20 }}>
              <Text strong>Generated Task ID:</Text>{" "}
              <Text code copyable={{ text: taskId }}>
                {taskId}
              </Text>
            </div>
          )}

          <div style={{ marginTop: 40 }}>
            <Title level={4}>2. Check Task Status & View Results</Title>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Input
                placeholder="Enter Task ID"
                value={statusCheckId}
                onChange={(e) => setStatusCheckId(e.target.value)}
              />
              <Button
                type="primary"
                loading={checkingStatus}
                onClick={() => checkTaskStatus()}
              >
                Check Status
              </Button>

              {taskData && (
                <div style={{ marginTop: 32 }}>
                  <Descriptions
                    bordered
                    size="small"
                    column={1}
                    title="Task Info"
                    labelStyle={{ width: 150 }}
                    contentStyle={{ width: 150 }}
                  >
                    <Descriptions.Item label="Status">
                      {taskData.status}
                    </Descriptions.Item>
                    {taskData.status === "queue" && (
                      <Descriptions.Item label="Position in Queue">
                        {taskData.position_in_queue ?? "N/A"} of{" "}
                        {taskData.queue_length ?? "?"}
                      </Descriptions.Item>
                    )}
                    {taskData.logs?.length > 0 && (
                      <>
                        <Descriptions.Item label="Step">
                          {taskData.logs.at(-1)?.step} / 10
                        </Descriptions.Item>
                        <Descriptions.Item label="Message">
                          {taskData.logs.at(-1)?.message}
                        </Descriptions.Item>
                        <Descriptions.Item label="Created">
                          {new Date(
                            taskData.logs[0].created_at
                          ).toLocaleString()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Updated">
                          {new Date(
                            taskData.logs.at(-1)?.updated_at
                          ).toLocaleString()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Elapsed Time">
                          {getElapsed(
                            taskData.logs[0].created_at,
                            taskData.logs.at(-1)?.updated_at
                          )}
                        </Descriptions.Item>
                      </>
                    )}
                    {taskData.logs?.at(-1)?.matched_snps != null && (
                      <Descriptions.Item label="Matched SNPs">
                        {taskData.logs.at(-1).matched_snps}
                      </Descriptions.Item>
                    )}
                    {taskData.logs?.at(-1)?.prs_score != null && (
                      <Descriptions.Item label="PRS Score">
                        {taskData.logs.at(-1).prs_score}
                      </Descriptions.Item>
                    )}
                    {taskData.logs?.at(-1)?.vcf_path && (
                      <Descriptions.Item label="VCF File">
                        <Button
                          icon={<DownloadOutlined />}
                          onClick={() =>
                            downloadFile(taskData.logs.at(-1).vcf_path, "VCF")
                          }
                          size="small"
                          type="primary"
                          loading={downloadingVCF}
                          disabled={downloadingVCF}
                        >
                          Download VCF
                        </Button>
                      </Descriptions.Item>
                    )}
                  </Descriptions>

                  {taskData.prs_result && (
                    <>
                      <Title level={5} style={{ marginTop: 24 }}>
                        Polygenic Risk Score Details
                      </Title>
                      <Descriptions bordered size="small" column={1}>
                        {Object.entries(taskData.prs_result).map(
                          ([key, value]) => (
                            <Descriptions.Item label={key} key={key}>
                              {value === null || value === undefined
                                ? "N/A"
                                : typeof value === "object"
                                ? JSON.stringify(value)
                                : String(value)}
                            </Descriptions.Item>
                          )
                        )}
                      </Descriptions>
                    </>
                  )}
                  {renderStepsSection()}
                </div>
              )}
            </Space>
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default HomePage;
